import { useEffect, useState } from "react";
import type { AdminAccount, AdminSummary, AllowlistEntry } from "@iltocat/shared";
import { api, formatBytes } from "../lib/api.js";
import { AccountsPanel } from "../components/AccountsPanel.js";
import { AllowlistPanel } from "../components/AllowlistPanel.js";
import { Icon, type IconName } from "../components/Icon.js";
import { Notice } from "../components/Notice.js";
import { PageHeader } from "../components/PageHeader.js";

/**
 * The console. It can invite, disable and count, and it can never open
 * anybody's inventory, which is a property of the routes behind it rather than
 * a promise made here.
 */
export function Admin() {
  const [summary, setSummary] = useState<AdminSummary | null>(null);
  const [allowlist, setAllowlist] = useState<AllowlistEntry[]>([]);
  const [accounts, setAccounts] = useState<AdminAccount[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void Promise.all([api.admin.summary(), api.admin.allowlist(), api.admin.accounts()])
      .then(([one, two, three]) => {
        if (!live) return;
        setSummary(one.summary);
        setAllowlist(two.allowlist);
        setAccounts(three.accounts);
      })
      .catch(() => {
        if (live) setError("Could not load the console.");
      });
    return () => {
      live = false;
    };
  }, []);

  const stats: { icon: IconName; label: string; value: string }[] = summary
    ? [
        { icon: "user", label: "Active accounts", value: String(summary.activeUsers) },
        { icon: "mail", label: "Invited", value: String(summary.allowlisted) },
        { icon: "box", label: "Inventories", value: String(summary.inventories) },
        { icon: "drive", label: "Stored", value: formatBytes(summary.usedBytes) },
      ]
    : [];

  return (
    <>
      <PageHeader title="Admin" />
      {error && <Notice tone="error">{error}</Notice>}

      <ul className="stats" aria-busy={summary === null}>
        {summary === null
          ? Array.from({ length: 4 }, (_, index) => <li key={index} className="stat skeleton" />)
          : stats.map((stat) => (
              <li key={stat.label} className="stat">
                <span className="stat-icon">
                  <Icon name={stat.icon} size={18} />
                </span>
                <span className="stat-value">{stat.value}</span>
                <span className="stat-label">{stat.label}</span>
              </li>
            ))}
      </ul>

      <AllowlistPanel allowlist={allowlist} onChange={setAllowlist} />
      <AccountsPanel accounts={accounts} onChange={setAccounts} />
    </>
  );
}
